import React, { createContext, useEffect, useState } from 'react'
import { Trans } from '@lingui/macro'
import PageHeader from '../../../components/common/page/header/PageHeader'

import IconChart from '../../../components/common/icon/IconChart'
import IconWrench from '../../../components/common/icon/IconWrench'
import IconPeople from '../../../components/common/icon/IconPeople'
import IconKey from '../../../components/common/icon/IconKey'
import { DashboardDataSetup } from './data-setup/DashboardDataSetup'
import { DashboardSummary } from '../summary/DashboardSummary'
import { Page } from '../../../components/common/page/Page'
import './ManageDashboardPage.less'
import { DashboardsDrawerWithGearButton } from '../DashboardsDrawerWithGearButton'
import Message, {
  MessageTypes
} from '../../../components/common/message/Message'
import IconDashboard from '../../../components/common/icon/IconDashboard'
import DashboardCollaborators from './collaborators/DashboardCollaborators'
import { DashboardAudience } from '../audience/DashboardAudience'
import {
  DashboardManageFragment,
  useGetDashboardQuery
} from './ManageDashboardPage.codegen'
import {
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from 'front-end/src/components'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { linkBuilder } from 'shared/linkBuilder'
import { useParams } from 'react-router-dom'

export const ManageDashboardPageContext = createContext<{
  dashboard: DashboardManageFragment
  refetch: () => void
  setIsLoading: (arg: boolean) => void
}>({
  dashboard: null as unknown as DashboardManageFragment,
  refetch: () => null,
  setIsLoading: () => {}
})

export const DASHBOARD_TABS = ['', 'setup', 'audience', 'collaborators']

export const ManageDashboardPage = () => {
  const { dashboardId: dashId, tab } =
    useParams<{ dashboardId: string; tab?: string }>()
  const [tabIndex, setTabIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const dashboardId = Number(dashId)
  const { data, refetch } = useGetDashboardQuery({
    variables: {
      id: dashboardId
    }
  })

  const getCurrentTab = () => {
    if (tab) {
      const currentIndex = DASHBOARD_TABS.findIndex((t) => t === tab)
      setTabIndex(currentIndex)
    } else {
      setTabIndex(0)
    }
  }

  useEffect(() => {
    getCurrentTab()
  }, [tab])

  if (!data || !data.admin) {
    return null
  }

  if (isLoading) {
    return <Spinner />
  }

  if (!data.admin.dashboard) {
    return (
      <Message type={MessageTypes.ERROR}>
        <Trans>Dashboard doesn't exist</Trans>
      </Message>
    )
  }

  const dashboard = data.admin.dashboard

  return (
    <ManageDashboardPageContext.Provider
      value={{ dashboard, refetch, setIsLoading }}
    >
      <Page className="ManageDashboardPage">
        <PageHeader
          className="PageHeader"
          title={
            <>
              <IconDashboard
                style={{ backgroundColor: dashboard.color || '#00bb77' }}
              />
              {dashboard.title}
            </>
          }
          titleRawText={dashboard.title}
          titleFollowers={
            dashboard.isEditor ? <DashboardsDrawerWithGearButton /> : undefined
          }
          subHeader={dashboard.description}
        />

        <Tabs mt="16px" index={tabIndex}>
          <TabList>
            <Tab
              onClick={() =>
                navigateTo(linkBuilder.dashboardManage(dashboardId))
              }
            >
              <IconChart mr="8px" />
              <Trans>Summary</Trans>
            </Tab>
            {dashboard.isEditor && (
              <>
                <Tab
                  onClick={() =>
                    navigateTo(
                      linkBuilder.dashboardManage(
                        dashboardId,
                        DASHBOARD_TABS[1]
                      )
                    )
                  }
                >
                  <IconWrench mr="8px" />
                  <Trans>Data setup</Trans>
                </Tab>
                <Tab
                  onClick={() =>
                    navigateTo(
                      linkBuilder.dashboardManage(
                        dashboardId,
                        DASHBOARD_TABS[2]
                      )
                    )
                  }
                >
                  <IconPeople mr="8px" />
                  <Trans>Audience</Trans>
                </Tab>
                <Tab
                  onClick={() =>
                    navigateTo(
                      linkBuilder.dashboardManage(
                        dashboardId,
                        DASHBOARD_TABS[3]
                      )
                    )
                  }
                >
                  <IconKey mr="8px" />
                  <Trans>Collaborators</Trans>
                </Tab>
              </>
            )}
          </TabList>

          <TabPanels bg="white" pt="16px">
            <TabPanel>
              <DashboardSummary
                dashboardId={dashboard.id}
                customMetricEnabled={dashboard.custom_metric}
              />
            </TabPanel>
            <TabPanel>
              <DashboardDataSetup
                dashboardId={dashboardId}
                dashboardData={dashboard}
              />
            </TabPanel>
            <TabPanel>
              <DashboardAudience />
            </TabPanel>
            <TabPanel>
              <DashboardCollaborators dashboardId={dashboardId} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Page>
    </ManageDashboardPageContext.Provider>
  )
}
